/**
 * Image Security Validator
 * Validates images using Magic Bytes to prevent malicious file uploads
 * 
 * Protects against:
 * - SVG with embedded JavaScript
 * - HTML files renamed as images
 * - Polyglot files (e.g., JPEG+HTML)
 * - Executable files with image extensions
 */

import { logger } from '@/lib/services/logger';

// Magic bytes signatures for real image formats
const IMAGE_SIGNATURES = {
  jpeg: { bytes: [0xFF, 0xD8, 0xFF], offset: 0 },
  png: { bytes: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], offset: 0 },
  gif87a: { bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], offset: 0 },
  gif89a: { bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], offset: 0 },
  webp: { 
    bytes: [0x52, 0x49, 0x46, 0x46], 
    offset: 0, 
    secondary: [0x57, 0x45, 0x42, 0x50], 
    secondaryOffset: 8 
  },
} as const;

// Dangerous patterns that indicate malicious content
const DANGEROUS_PATTERNS = [
  '<script',
  'javascript:',
  'vbscript:',
  'onerror=',
  'onload=',
  'onclick=',
  'onmouseover=',
  'onfocus=',
  'onblur=',
  'oninput=',
  'onchange=',
  'onsubmit=',
  'eval(',
  'expression(',
  '<?xml',
  '<svg',
  '<!doctype',
  '<html',
  '<head',
  '<body',
  '<iframe',
  '<object',
  '<embed',
  '<form',
  'data:text/html',
  'data:application/javascript',
] as const;

export interface ImageValidationResult {
  valid: boolean;
  detectedFormat: 'jpeg' | 'png' | 'gif' | 'webp' | null;
  error?: string;
}

/**
 * Check if bytes at offset match the expected pattern
 */
function matchBytes(data: Uint8Array, pattern: readonly number[], offset: number): boolean {
  if (data.length < offset + pattern.length) return false;
  for (let i = 0; i < pattern.length; i++) {
    if (data[offset + i] !== pattern[i]) return false;
  }
  return true;
}

/**
 * Validates file header bytes against known image format signatures
 */
export async function validateImageMagicBytes(file: File): Promise<ImageValidationResult> {
  try {
    // Read first 16 bytes for signature check
    const headerBuffer = await file.slice(0, 16).arrayBuffer();
    const header = new Uint8Array(headerBuffer);

    // Check JPEG (FFD8FF)
    if (matchBytes(header, IMAGE_SIGNATURES.jpeg.bytes, 0)) {
      return { valid: true, detectedFormat: 'jpeg' };
    }

    // Check PNG (89504E470D0A1A0A)
    if (matchBytes(header, IMAGE_SIGNATURES.png.bytes, 0)) {
      return { valid: true, detectedFormat: 'png' };
    }

    // Check GIF87a
    if (matchBytes(header, IMAGE_SIGNATURES.gif87a.bytes, 0)) {
      return { valid: true, detectedFormat: 'gif' };
    }

    // Check GIF89a
    if (matchBytes(header, IMAGE_SIGNATURES.gif89a.bytes, 0)) {
      return { valid: true, detectedFormat: 'gif' };
    }

    // Check WebP (RIFF....WEBP)
    if (matchBytes(header, IMAGE_SIGNATURES.webp.bytes, 0)) {
      const extendedBuffer = await file.slice(0, 12).arrayBuffer();
      const extended = new Uint8Array(extendedBuffer);
      if (matchBytes(extended, IMAGE_SIGNATURES.webp.secondary, IMAGE_SIGNATURES.webp.secondaryOffset)) {
        return { valid: true, detectedFormat: 'webp' };
      }
    }

    return {
      valid: false,
      detectedFormat: null,
      error: 'Файл не является изображением (неверная сигнатура)',
    };
  } catch (error) {
    logger.error('Error validating image magic bytes', {
      context: { feature: 'image-security' },
      error: error as Error,
      error_code: 'magic_bytes_validation_failed',
      error_type: 'unexpected',
    });
    return {
      valid: false,
      detectedFormat: null,
      error: 'Не удалось проверить файл',
    };
  }
}

/**
 * Scans file content for dangerous patterns (scripts, HTML, etc.)
 */
export async function checkForDangerousContent(
  file: File
): Promise<{ safe: boolean; threat?: string }> {
  try {
    // Read first 2KB for pattern scanning
    const sampleSize = Math.min(file.size, 2048);
    const sampleBuffer = await file.slice(0, sampleSize).arrayBuffer();
    const sample = new TextDecoder('utf-8', { fatal: false })
      .decode(sampleBuffer)
      .toLowerCase();

    for (const pattern of DANGEROUS_PATTERNS) {
      if (sample.includes(pattern.toLowerCase())) {
        logger.warn('Dangerous content detected in file', {
          context: { feature: 'image-security' },
          data: { pattern, fileName: file.name },
          allowList: ['pattern', 'fileName'],
        });
        return {
          safe: false,
          threat: `Обнаружен запрещённый контент`,
        };
      }
    }

    return { safe: true };
  } catch (error) {
    // On read error, allow the file (don't block legitimate files)
    logger.warn('Error checking file content, allowing', {
      context: { feature: 'image-security' },
      error: error as Error,
    });
    return { safe: true };
  }
}

/**
 * Complete security validation for image files
 * Checks both magic bytes and dangerous content
 */
export async function validateImageSecurity(file: File): Promise<ImageValidationResult> {
  // Step 1: Validate magic bytes
  const magicResult = await validateImageMagicBytes(file);
  if (!magicResult.valid) {
    return magicResult;
  }

  // Step 2: Scan for dangerous content (polyglot files)
  const contentResult = await checkForDangerousContent(file);
  if (!contentResult.safe) {
    return {
      valid: false,
      detectedFormat: null,
      error: contentResult.threat,
    };
  }

  return magicResult;
}

/**
 * Quick sync check for file extension safety
 * Use before async validation for fast rejection
 */
export function hasValidImageExtension(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
}

/**
 * Check if MIME type matches detected format
 */
export function mimeMatchesFormat(
  mimeType: string, 
  detectedFormat: ImageValidationResult['detectedFormat']
): boolean {
  if (!detectedFormat) return false;
  
  const mimeMap: Record<string, string[]> = {
    jpeg: ['image/jpeg', 'image/jpg'],
    png: ['image/png'],
    gif: ['image/gif'],
    webp: ['image/webp'],
  };
  
  return mimeMap[detectedFormat]?.includes(mimeType) ?? false;
}
